import { Link } from "react-router-dom";
import { Phone, Mail, Clock, ArrowRight, MapPin, Instagram, Facebook } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useQuoteForm } from "@/components/QuoteFormContext";
import { siteConfig } from "@/lib/site-config";
import { services } from "@/lib/services-data";
import { locations } from "@/lib/locations-data";
import TikTokIcon from "@/components/TikTokIcon";
import ResponsiveImage from "@/components/ResponsiveImage";

const Footer = () => {
  const { openQuoteForm } = useQuoteForm();

  return (
    <footer className="bg-accent-dark text-white">
      {/* CTA Banner — solid dark, no gradient */}
      <div className="bg-primary">
        <div className="container-max px-4 py-12 md:py-14">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl text-primary-foreground font-bold tracking-tight">
                Ready to Start Your Project?
              </h2>
              <p className="text-primary-foreground/85 text-sm md:text-base mt-1">
                Free estimates across northern New Jersey · {siteConfig.hours.summary}.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                variant="secondary"
                size="lg"
                onClick={openQuoteForm}
                className="gap-2 rounded-xl font-bold"
              >
                Get Free Estimate <ArrowRight className="h-5 w-5" />
              </Button>
              <div>
                <Button asChild size="lg" className="gap-2 rounded-xl font-bold bg-white text-foreground hover:bg-white/90">
                  <a href={siteConfig.phoneHref}>
                    <Phone className="h-4 w-4" /> {siteConfig.phone}
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer content — solid dark BG, no gradient */}
      <div className="container-max section-padding">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
          <div>
            {/* The logo is a dark-background mark, so it needs no white card. */}
            <div className="inline-flex items-center justify-center rounded-2xl overflow-hidden shadow-md mb-5">
              <ResponsiveImage sizes="161px"
                src={siteConfig.logo}
                alt={siteConfig.name}
                className="h-20 w-auto"
                width={800}
                height={397}
                loading="lazy"
                decoding="async"
              />
            </div>
            <h3 className="font-heading text-xl text-white font-bold mb-2 tracking-tight">
              {siteConfig.name}
            </h3>
            <p className="text-white/90 text-sm leading-relaxed">
              Remodeling and construction for homes and properties across {siteConfig.address.city}{" "}
              and northern New Jersey — {siteConfig.hours.days}, {siteConfig.hours.time}. Closed{" "}
              {siteConfig.hours.closed}. {siteConfig.languages}.
            </p>
            <div className="mt-4 bg-primary/15 border border-primary/30 rounded-xl px-4 py-3">
              <p className="text-white font-semibold text-sm">
                🎁 {siteConfig.discount.label}
              </p>
            </div>
          </div>

          <div>
            <h3 className="font-heading text-lg tracking-tight mb-4 text-white font-bold">
              Services
            </h3>
            <ul className="space-y-2.5">
              {services.map((s) => (
                <li key={s.slug}>
                  <Link
                    to={`/services/${s.slug}`}
                    className="footer-link-underline text-white/90 hover:text-white text-sm transition-colors"
                  >
                    {s.title}
                  </Link>
                </li>
              ))}
              <li className="pt-2">
                <Link
                  to="/services"
                  className="footer-link-underline text-primary hover:text-primary/80 text-sm font-semibold transition-colors"
                >
                  View All Services →
                </Link>
              </li>
              <li>
                <Link
                  to="/contact"
                  className="footer-link-underline text-primary hover:text-primary/80 text-sm font-semibold transition-colors"
                >
                  Request an Estimate →
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="font-heading text-lg tracking-tight mb-4 text-white font-bold">
              Service Areas
            </h3>
            <ul className="grid grid-cols-2 gap-x-2 gap-y-2 text-sm">
              {locations.slice(0, 8).map((loc) => (
                <li key={loc.slug}>
                  <Link
                    to={`/locations/${loc.slug}`}
                    className="footer-link-underline text-white/90 hover:text-white transition-colors"
                  >
                    {loc.name}, {loc.state}
                  </Link>
                </li>
              ))}
            </ul>
            <Link
              to="/locations"
              className="mt-3 inline-block footer-link-underline text-primary hover:text-primary/80 text-sm font-semibold transition-colors"
            >
              All Locations →
            </Link>
          </div>

          <div>
            <h3 className="font-heading text-lg tracking-tight mb-4 text-white font-bold">
              Contact
            </h3>
            <ul className="space-y-3 text-sm text-white/90">
              <li className="flex items-start gap-2">
                <Phone className="h-4 w-4 shrink-0 text-primary mt-0.5" />
                <a href={siteConfig.phoneHref} className="hover:text-white transition-colors">
                  {siteConfig.phone}
                </a>
              </li>
              <li className="flex items-start gap-2">
                <Mail className="h-4 w-4 shrink-0 text-primary mt-0.5" />
                <a href={siteConfig.emailHref} className="hover:text-white transition-colors break-all">
                  {siteConfig.email}
                </a>
              </li>
              <li className="flex items-start gap-2">
                <MapPin className="h-4 w-4 shrink-0 text-primary mt-0.5" />
                <span>
                  {siteConfig.address.city}, {siteConfig.address.state}{" "}
                  {siteConfig.address.postalCode}
                </span>
              </li>
              <li className="flex items-start gap-2">
                <Clock className="h-4 w-4 shrink-0 text-primary mt-0.5" />
                <div>
                  <div>{siteConfig.hours.days}</div>
                  <div className="text-white/90 text-xs">{siteConfig.hours.time}</div>
                  <div className="text-white/85 text-xs">Closed {siteConfig.hours.closed}</div>
                </div>
              </li>
            </ul>

            <div className="mt-5 flex items-center gap-3">
              {siteConfig.social.tiktok && (
                <a
                  href={siteConfig.social.tiktok}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`${siteConfig.name} on TikTok`}
                  className="inline-flex items-center justify-center h-9 w-9 rounded-lg bg-white/10 hover:bg-primary hover:text-primary-foreground text-white transition-colors"
                >
                  <TikTokIcon className="h-4 w-4" />
                </a>
              )}
              {siteConfig.social.instagram && (
                <a
                  href={siteConfig.social.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`${siteConfig.name} on Instagram`}
                  className="inline-flex items-center justify-center h-9 w-9 rounded-lg bg-white/10 hover:bg-primary hover:text-primary-foreground text-white transition-colors"
                >
                  <Instagram className="h-4 w-4" />
                </a>
              )}
              {siteConfig.social.facebook && (
                <a
                  href={siteConfig.social.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`${siteConfig.name} on Facebook`}
                  className="inline-flex items-center justify-center h-9 w-9 rounded-lg bg-white/10 hover:bg-primary hover:text-primary-foreground text-white transition-colors"
                >
                  <Facebook className="h-4 w-4" />
                </a>
              )}
            </div>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4">
          {/* The prerendered year can trail the visitor's clock across New Year. */}
          <p className="text-white/90 text-xs" suppressHydrationWarning>
            © {new Date().getFullYear()} {siteConfig.legalName}. All rights reserved.
          </p>
          <p className="text-white/90 text-xs">
            Remodeling & Construction · Clifton & Northern New Jersey
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
